import type { ExitReviewResponse } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchExitReviewApi } from "../../../api/client";
import { exitReviewKeys } from "./exitReviewKeys";

/**
 * Shared queryOptions for the Workbench exit review (key + queryFn). Backed by the
 * generated WorkbenchExitReviewApi.getExitReview, which returns the next-status /
 * phase dropdown options (`nextStatusPhases`) and the Review Advances figures
 * (`openStipulationCount`, `internalAdvance`, `dealStatus`). A leg the BFF could not
 * load is absent and listed in `metadata.warnings`.
 *
 * All three inputs are required by the BFF, so the query stays disabled until
 * `approvalId`, `currentStatus`, and `roleName` are present (and the caller opts in
 * via `enabled`) — this keeps the request from firing before the application-status
 * read resolves or while the modal is closed.
 *
 * `staleTime` is 0: the figures are money and stipulation counts that change as the
 * analyst works the deal, so reopening the modal must not show a cached read.
 * @param {string} approvalId - The deal / approval identifier (path param).
 * @param {string} currentStatus - The deal's current status (query param).
 * @param {string} roleName - The analyst's role name (query param).
 * @param {boolean} enabled - Caller opt-in; combined with the presence guards.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function exitReviewQuery(approvalId: string, currentStatus: string, roleName: string, enabled = true) {
  return queryOptions({
    queryKey: exitReviewKeys.detail({ approvalId, currentStatus, roleName }),
    queryFn: async ({ signal }): Promise<ExitReviewResponse> => {
      const response = await workbenchExitReviewApi.getExitReview(approvalId, currentStatus, roleName, { signal });
      return response.data;
    },
    staleTime: 0,
    enabled: enabled && !!approvalId && !!currentStatus && !!roleName,
  });
}

/**
 * Reads the exit review for the exit modal. Disabled until the approval id, current
 * status, and role name are all present and the caller enables it (e.g. only while
 * the exit modal is open).
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} currentStatus - The deal's current status.
 * @param {string} roleName - The analyst's role name.
 * @param {{ enabled?: boolean }} [options] - Caller opt-in (defaults to enabled).
 * @param {boolean} [options.enabled] - When false, the query never fires.
 * @returns {ReturnType<typeof useQuery<ExitReviewResponse>>} The TanStack Query result.
 */
export function useExitReview(
  approvalId: string,
  currentStatus: string,
  roleName: string,
  options?: { enabled?: boolean }
) {
  return useQuery(exitReviewQuery(approvalId, currentStatus, roleName, options?.enabled ?? true));
}
